import {
  dailyChartAxisLabel,
  dailyChartBarAxisProps,
  dailyChartRotateAxisLabels,
  formatDashboardDayLabelLong,
} from "../../lib/dashboardDayLabel.js";
import type { DailyUptimeRow } from "../../types.js";
import { ChartCard } from "../ChartCard.js";
import { BarChart, type BarChartItem } from "../charts/BarChart.js";

function formatUptimeHoursLabel(hours: number): string {
  const t = hours.toFixed(1);
  return t.endsWith(".0") ? t.slice(0, -2) : t;
}

function uptimeDayMixBarData(rows: DailyUptimeRow[]): BarChartItem[] {
  return rows.map((row) => {
    const cap = row.capacityHours;
    const axis = dailyChartBarAxisProps(row.day);
    if (cap <= 1e-6) {
      return { ...axis, value: 0, valueLabel: "—", stack: [] };
    }
    const up = Math.max(0, row.uptimeHours);
    const sched = Math.max(0, row.scheduledHours);
    const unsched = Math.max(0, row.unscheduledHours);
    const sum = up + sched + unsched + row.unmeasuredHours;
    if (sum <= 1e-9) {
      return { ...axis, value: 0, valueLabel: "0", stack: [] };
    }
    return {
      ...axis,
      /** Stack sum ≈ capacity so far; yMax scales with table count. */
      value: sum,
      valueLabel: row.unmeasuredHours >= cap ? "—" : formatUptimeHoursLabel(up),
      stack: [
        { value: up, fill: "var(--color-ok)" },
        { value: sched, fill: "var(--color-warn)" },
        { value: unsched, fill: "#7a3548" },
        { value: row.unmeasuredHours, fill: "#6b7280" },
      ],
    };
  });
}

function dayCapacityMixTooltip(row: DailyUptimeRow): string {
  const up = row.uptimeHours.toFixed(1);
  const sched = row.scheduledHours.toFixed(1);
  const unsched = row.unscheduledHours.toFixed(1);
  const total = row.capacityHours.toFixed(1);
  return `${formatDashboardDayLabelLong(row.day)}: Uptime ${up}h, Scheduled Downtime ${sched}h, No signal ${unsched}h, Not measured ${row.unmeasuredHours.toFixed(1)}h (Total capacity ${total}h across tables)`;
}

const DAY_CAPACITY_CRITERIA = `Fleet additive per-day **uptime**, **scheduled**, and **no signal**, and **not measured** hours across **all tables** in the current filter scope (capacity = tables × reporting-day hours). Same sources as Single Table Uptime Breakdown, summed — not wall-clock union.

**Scheduled** (yellow) uses the venue **scheduled_downtime** config when set; otherwise sleep tags / legacy downtime events. **Uptime** (green) is measured table life; **gap** (dark) is the remainder.

**Today** uses elapsed reporting-day capacity only (API \`capacityMs\` / hours so far), so the bar is shorter than a completed day — not stretched to 100%.

Top meta row: table count in scope. **Uptime** sits to the left of the hour numbers above each bar. Numbers are **uptime hours** (whole numbers when exact; else 1 decimal). Hover for full day breakdown.

Axis labels: **D/M** + weekday initial (e.g. **31/7 F**); Sat/Sun bold white. Dense ranges rotate −90° (default short-of-space day axis).

Not a single event_name series; clicks not wired.`;

interface DayCapacityMixCardProps {
  dailyUptime: DailyUptimeRow[];
  tableCount: number;
  /** When false, hides the ⓘ criteria toggle (Overview / PDF). */
  showCriteria?: boolean;
}

export function DayCapacityMixCard({
  dailyUptime,
  tableCount,
  showCriteria = true,
}: DayCapacityMixCardProps) {
  const tables = Math.max(0, tableCount);
  const yMax = Math.max(24, ...dailyUptime.map((row) => row.capacityHours));

  return (
    <ChartCard
      title="Day capacity mix"
      criteria={showCriteria ? DAY_CAPACITY_CRITERIA : undefined}
      note={`Bar height = table-hours so far (max ${yMax}h for ${tables} table${tables === 1 ? "" : "s"}). Today is partial. Platform heartbeat minutes; grey = not measured.`}
    >
      <BarChart
        data={uptimeDayMixBarData(dailyUptime)}
        fillBars
        legend={[
          { label: "Uptime", fill: "var(--color-ok)" },
          { label: "Scheduled", fill: "var(--color-warn)" },
          { label: "No signal", fill: "#7a3548" },
          { label: "Not measured", fill: "#6b7280" },
        ]}
        yMax={yMax}
        compact={dailyChartRotateAxisLabels(dailyUptime.length)}
        verbatimAxisLabels
        rotateAxisLabels={dailyChartRotateAxisLabels(dailyUptime.length)}
        axisLabelMaxChars={10}
        valueLabelAxisMeta={tables === 1 ? "1 table" : `${tables} tables`}
        valueLabelAxisTitle="Uptime"
        hideStackSegmentLabels
        barTooltip={(item) => {
          const row = dailyUptime.find((r) => dailyChartAxisLabel(r.day) === item.label);
          if (!row) return item.label;
          return dayCapacityMixTooltip(row);
        }}
      />
    </ChartCard>
  );
}
